import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getClientes, crearCliente, actualizarCliente } from "../../../services/usuarios_tienda/clientes";
import CustomerFormModal from "./CustomerFormModal";
import CustomerAddressesModal from "./CustomerAddressesPage";
import { IconEdit, IconLocation } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./CustomersPage.css";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const VISIBLE_PAGE_COUNT = 3;

const normalizePhoneCode = (v) => {
  const raw = String(v ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  if (/^\d+$/.test(raw)) return `+${raw}`;
  return raw;
};

const formatFullPhone = (code, phone) => {
  const c = normalizePhoneCode(code);
  const p = String(phone ?? "").trim();
  if (c && p) return `${c} ${p}`;
  if (p) return p;
  return c || "";
};

function CustomersPage() {
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [selectedAddressCustomer, setSelectedAddressCustomer] = useState(null);
  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  const cargarClientes = useCallback(async () => {
    try {
      const data = await getClientes();
      setClientes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando clientes:", error);
      showAlert("error", error?.message || "No se pudieron cargar los clientes.");
      setClientes([]);
    }
  }, [showAlert]);

  useEffect(() => {
    cargarClientes();
  }, [cargarClientes]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clientes;

    return clientes.filter((c) => {
      const blob = [
        c?.cliente_id,
        c?.nombre,
        c?.apellido,
        c?.correo,
        c?.telefono,
        c?.codigo_telefono,
        c?.codigo_pais,
        c?.country_code,
        c?.phone_country_code,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return blob.includes(q);
    });
  }, [clientes, search]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / pageSize)),
    [filtered.length, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const visiblePages = useMemo(() => {
    const total = totalPages;
    if (total <= VISIBLE_PAGE_COUNT) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, page - Math.floor(VISIBLE_PAGE_COUNT / 2));
    let endPage = startPage + VISIBLE_PAGE_COUNT - 1;
    if (endPage > total) {
      endPage = total;
      startPage = endPage - VISIBLE_PAGE_COUNT + 1;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [page, totalPages]);

  const goToPage = (pageNumber) => {
    if (pageNumber === page || pageNumber < 1 || pageNumber > totalPages) return;
    setPage(pageNumber);
  };

  const nextPage = () => hasNext && setPage(page + 1);
  const prevPage = () => hasPrev && setPage(page - 1);

  const openCreateModal = () => {
    setSelectedCustomer(null);
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const handleSubmit = async (form) => {
    try {
      if (selectedCustomer) {
        const res = await actualizarCliente(selectedCustomer.cliente_id, form);
        showAlert("success", res?.message || "Cliente actualizado correctamente.");
      } else {
        const res = await crearCliente(form);
        showAlert("success", res?.message || "Cliente creado correctamente.");
      }

      await cargarClientes();
      setShowModal(false);
    } catch (error) {
      console.error("Error guardando cliente:", error);
      throw error;
    }
  };

  return (
    <div className="tienda-page-scope">
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button
            type="button"
            className="omx-alert__x"
            onClick={() => setAlert(null)}
            aria-label="Cerrar alerta"
          >
            ×
          </button>
        </div>
      )}

      <div className="header">
        <div className="customers-header__row">
          <h2 className="title">Gestión de clientes</h2>

          {hasPerm("create_customer") ? (
            <button type="button" className="customers-btn-create" onClick={openCreateModal}>
              Crear cliente
            </button>
          ) : null}
        </div>

        <p className="page-lead">
          Consulta y administra los clientes de tu tienda. Búscalos por nombre, correo o teléfono.
        </p>

        <div className="customers-filters">
          <div className="customers-filters__group customers-filters__group--search">
            <label className="customers-filters__label" htmlFor="customers-search">
              Buscar
            </label>
            <div className="customers-filters__search-wrap">
              <input
                id="customers-search"
                type="text"
                placeholder="Nombre, correo, teléfono…"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.trim() ? (
                <button
                  type="button"
                  className="customers-filters__clear"
                  onClick={() => setSearch("")}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
          <table className="modern-table customers-table tienda-table--cards">
            <thead>
              <tr>
                <th className="customers-col-id">ID</th>
                <th>Nombre</th>
                <th>Apellido</th>
                <th>Teléfono</th>
                <th>Correo</th>
                <th className="customers-col-fecha">Creado</th>
                <th className="customers-col-actions" style={{ textAlign: "right" }}>
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} className="no-data">
                    No se encontraron clientes
                  </td>
                </tr>
              ) : (
                paginated.map((c) => {
                  const phoneCode =
                    c?.codigo_telefono ??
                    c?.codigo_pais ??
                    c?.country_code ??
                    c?.phone_country_code ??
                    "";

                  return (
                    <tr key={c.cliente_id} className="customers-row">
                      <td className="customers-col-id" data-label="ID">
                        {c.cliente_id}
                      </td>
                      <td data-label="Nombre">
                        <span className="customers-name">{c.nombre}</span>
                      </td>
                      <td data-label="Apellido">{c.apellido}</td>
                      <td data-label="Teléfono">
                        {formatFullPhone(phoneCode, c.telefono) || "—"}
                      </td>
                      <td data-label="Correo">{c.correo}</td>
                      <td className="customers-col-fecha" data-label="Creado">
                        {c.fecha_creado?.slice(0, 10) || "—"}
                      </td>

                      <td
                        className="customers-col-actions"
                        data-label="Acciones"
                        style={{ textAlign: "right" }}
                      >
                        <div className="customers-actions">
                          {hasPerm("update_customer") && (
                            <span
                              className="action-icon edit"
                              onClick={() => openEditModal(c)}
                              title="Editar cliente"
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === "Enter" && openEditModal(c)}
                            >
                              {IconEdit}
                            </span>
                          )}

                          {hasPerm("update_direccion_customer") && (
                            <span
                              className="action-icon"
                              onClick={() => {
                                setSelectedAddressCustomer(c);
                                setShowAddressModal(true);
                              }}
                              title="Ver direcciones"
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  setSelectedAddressCustomer(c);
                                  setShowAddressModal(true);
                                }
                              }}
                            >
                              {IconLocation}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="pagination">
            <div className="pagination__center">
              <div className="pagination__nav" aria-label="Paginación de clientes">
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={prevPage}
                  disabled={!hasPrev}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <div className="pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`pagination__page${
                        pageNumber === page ? " is-active" : ""
                      }`}
                      onClick={() => goToPage(pageNumber)}
                      aria-label={`Ir a página ${pageNumber}`}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={nextPage}
                  disabled={!hasNext}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <div className="pagination__size">
                <span className="pagination__size-label">Mostrar</span>
                <select
                  className="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  title="Filas por página"
                  aria-label="Filas por página"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="pagination__size-label">por página</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <CustomerFormModal
          isOpen={showModal}
          customerData={selectedCustomer}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}

      {showAddressModal && (
        <CustomerAddressesModal
          isOpen={showAddressModal}
          cliente={selectedAddressCustomer}
          onClose={() => setShowAddressModal(false)}
        />
      )}
    </div>
  );
}

export default CustomersPage;
